import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import * as protomapsL from "protomaps-leaflet"

// Markers are drawn rather than imported. Leaflet's default is a blue PNG that
// belongs to no brand, and bundlers get its icon paths wrong anyway — this
// sidesteps both.
//
// Two shapes and four colours, and they answer two different questions. The
// shape says what kind of place it is: a Cirkelhus is a house, somewhere you
// can walk into any week, and a lokal cirkel — a venue an activity is held at
// — is a pin. The colour says which part of the kommune it is in, the same
// four colours the calendar filter uses.
//
// The two never stand on colour alone: the shape separates house from cirkel,
// and the popup names the zone in words (WCAG 1.4.1). The legend under the map
// says both.
function houseIcon({ color, name }) {
  const size = 46

  // Black outline and a soft drop shadow so a pale palette colour still reads
  // against grass, water or a pale road.
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 42" width="${size}" height="${size * 42 / 40}" aria-hidden="true" focusable="false">
      <g style="filter: drop-shadow(0 2px 3px rgba(0,0,0,.35))">
        <path d="M20 3 37.5 18.5V36a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 36V18.5Z"
              fill="${color}" stroke="#0b0b0b" stroke-width="2.5" stroke-linejoin="round"/>
        <path d="M15.5 38.5V26.5h9v12" fill="#0b0b0b" opacity=".8"/>
      </g>
    </svg>`

  return L.divIcon({
    html: `${svg}<span class="sr-only">${escapeHtml(name)}</span>`,
    className: "",                                  // no Leaflet default styling
    iconSize: [ size, size * 42 / 40 ],
    iconAnchor: [ size / 2, size * 42 / 40 ],       // stands on the spot
    popupAnchor: [ 0, -size * 40 / 40 ]
  })
}

function pinIcon({ color, name }) {
  const size = 34

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44" width="${size}" height="${size * 44 / 32}" aria-hidden="true" focusable="false">
      <path d="M16 43C16 43 29 26.5 29 16.5 29 8.5 23.2 2 16 2S3 8.5 3 16.5C3 26.5 16 43 16 43Z"
            fill="${color}" stroke="#0b0b0b" stroke-width="2.5" stroke-linejoin="round"
            style="filter: drop-shadow(0 2px 3px rgba(0,0,0,.35))"/>
      <circle cx="16" cy="15" r="4" fill="#0b0b0b" opacity=".55"/>
    </svg>`

  // The name lives inside the icon rather than being set on the element
  // afterwards. Leaflet rebuilds the marker element on zoom — fitBounds alone
  // is enough — and an attribute set from outside is discarded when it does.
  // A role="button" takes its accessible name from its contents, so this one
  // survives.
  return L.divIcon({
    html: `${svg}<span class="sr-only">${escapeHtml(name)}</span>`,
    className: "",                       // no Leaflet default styling
    iconSize: [ size, size * 44 / 32 ],
    iconAnchor: [ size / 2, size * 44 / 32 ],   // the point, not the middle
    popupAnchor: [ 0, -size * 40 / 32 ]
  })
}

// Map of the Cirkelhuse and the venues activities are held at.
//
// Supplementary by design: the cards underneath carry the same information as
// text, so nothing is only available by reading a map. The container is
// keyboard-operable (Leaflet handles arrow-key panning and +/- zoom) and
// labelled, but a visitor who cannot use it loses nothing.
//
// Where the tiles come from is configuration, not code: see map_tile_url in
// config/cirkler.yml. Switching provider — to a Danish public source, to tiles
// we serve ourselves, to anything else — is a line in that file rather than a
// change here, so it needs no developer and no deploy of new logic.
export default class extends Controller {
  static values = {
    places: Array,
    tileUrl: String,
    attribution: String,
    // Roskilde town centre, used only when nothing is geocoded yet.
    fallbackCenter: { type: Array, default: [55.6415, 12.0803] },
    zoom: { type: Number, default: 11 },
    minZoom: { type: Number, default: 9 },
    // Everything Cirkler uses sits within about 14 km of the two houses; the
    // one exception is 80 km away. 40 km separates them with room to spare.
    localRadiusKm: { type: Number, default: 40 },
    // The deepest zoom the source offers. Past it the map would ask for tiles
    // that do not exist and draw grey squares.
    maxZoom: { type: Number, default: 18 },
    // Where the source's data ends, south-west corner then north-east, from
    // config. The map cannot be panned beyond it, so nobody is left looking
    // at the grey outside the data. Empty means no edge.
    bounds: { type: Array, default: [] }
  }

  // One file of vector tiles on our own domain, drawn in the browser, or a
  // {z}/{x}/{y} template from wherever config points. Either way the choice
  // is made in config/cirkler.yml, not here. See docs/kort.md.
  baseLayer() {
    if (this.tileUrlValue.endsWith(".pmtiles")) {
      return protomapsL.leafletLayer({
        url: this.tileUrlValue,
        flavor: "light",
        lang: "da",
        maxDataZoom: 15,
        maxZoom: this.maxZoomValue,
        attribution: this.attributionValue
      })
    }

    return L.tileLayer(this.tileUrlValue, { maxZoom: this.maxZoomValue, attribution: this.attributionValue })
  }

  connect() {
    const located = this.placesValue.filter((p) => p.lat != null && p.lng != null)

    // minZoom keeps the municipality readable. A single far-off outing venue
    // — a day trip to the other end of Sjælland — would otherwise drag
    // fitBounds out to the whole country and shrink Roskilde to a dot. Those
    // markers are still there to pan to, and every venue is listed as text
    // below the map regardless.
    this.map = L.map(this.element, {
      scrollWheelZoom: false,
      minZoom: this.minZoomValue,
      maxBounds: this.boundsValue.length > 0 ? this.boundsValue : undefined,
      // A hard edge rather than a rubber band: there is nothing to see past it.
      maxBoundsViscosity: 1
    })
    // Leaflet's default attribution prefix is a link to leafletjs.com with a
    // Ukrainian flag rendered inline (see ukrainianFlag in leaflet-src.js).
    // A kommune's website should not carry a political statement its elected
    // officials have not made, least of all one arriving through a JavaScript
    // dependency, so the prefix is dropped.
    //
    // The map data credit below stays: the licence on the map data requires
    // it. Leaflet's own BSD licence asks only that its copyright notice
    // travels with the source, which it does — it does not require a link in
    // the interface.
    this.map.attributionControl.setPrefix(false)

    this.baseLayer().addTo(this.map)

    if (located.length === 0) {
      this.map.setView(this.fallbackCenterValue, this.zoomValue)
      return
    }

    const markers = located.map((place) => {
      const label = markerName(place)
      const marker = L.marker([place.lat, place.lng], {
        icon: place.anchor ? houseIcon({ color: place.color, name: label }) : pinIcon({ color: place.color, name: label }),
        title: label,
        alt: label,
        // Houses sit above the venues where pins overlap around Roskilde.
        zIndexOffset: place.anchor ? 1000 : 0,
        keyboard: true
      })
      marker.bindPopup(this.popupFor(place))
      marker.addTo(this.map)

      return marker
    })

    // Frame on everything nearby, so no local venue is cut off. A one-off day
    // trip to the other end of the country is left out of the initial view —
    // it would otherwise drag the map out to show all of Denmark and shrink
    // Roskilde to a dot. It is still there to pan or zoom out to, and it is
    // listed as text on the page like every other venue.
    //
    // Measured from the Cirkelhuse rather than the mean of all markers, so one
    // distant outlier cannot pull the centre away from where people actually go.
    const anchors = located.filter((p) => p.anchor)
    const centre  = anchors.length > 0 ? anchors : located
    const centreLat = average(centre.map((p) => p.lat))
    const centreLng = average(centre.map((p) => p.lng))

    const nearby = located.filter((p) => distanceKm(centreLat, centreLng, p.lat, p.lng) <= this.localRadiusKmValue)
    const framed = nearby.length > 0 ? nearby : located

    this.map.fitBounds(L.latLngBounds(framed.map((p) => [p.lat, p.lng])), { padding: [50, 50] })
  }

  disconnect() {
    this.map?.remove()
  }

  // The activities, and nothing else — a pin answers "what is on?" before "what
  // is this building called?", and each name is a link to the activity, where
  // you sign up. No address and no zone line: the venue is on the page as text
  // below the map, and the zone is in the marker's accessible name. A venue
  // with nothing to sign up for falls back to its own name, and a Cirkelhus is
  // always called by its name.
  popupFor(place) {
    const activities = place.activities || []
    if (activities.length === 0) return `<strong>${escapeHtml(place.name)}</strong>`

    return activities.map((activity) =>
      `<a class="ck-link" href="${escapeHtml(activity.url)}"><strong>${escapeHtml(activity.title)}</strong></a>`
    ).join("<br>")
  }
}

// The marker's accessible name. A screen reader hears what is held at the
// venue as well as the venue itself, so a pin is as informative to read as it
// is to look at. The zone rides along here rather than in the popup, so the
// marker's colour is never the only way to tell which part of the kommune a
// place is in (WCAG 1.4.1).
function markerName(place) {
  const titles = (place.activities || []).map((activity) => activity.title)
  const named  = titles.length > 0 ? `${titles.join(", ")} - ${place.name}` : place.name
  return place.zone ? `${named} - ${place.zone}` : named
}

function average(values) {
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

// Great-circle distance, good enough to tell "in the kommune" from "a day trip".
function distanceKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * 6371 * Math.asin(Math.sqrt(a))
}

// Venue and activity names are admin-entered, so they are escaped before
// going anywhere near innerHTML.
function escapeHtml(value) {
  const div = document.createElement("div")
  div.textContent = String(value)
  return div.innerHTML
}
